import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { apiClient } from "@finitless/shared/lib";
import { classifyMenuImportError } from "@/lib/menuImportErrors";
import { resolveSourceUrl, saveRun } from "@/lib/menuImportRuns";
import { fetchBusinessMenuSnapshot } from "./useBusinessMenuSnapshot";

class MenuImportFailure extends Error {
  constructor(message, { retryable, outcome }) {
    super(message);
    this.name = "MenuImportFailure";
    this.retryable = retryable;
    this.outcome = outcome;
  }
}

const menuImportApiUrl = () =>
  (import.meta.env.VITE_MENU_IMPORT_API_URL || "").trim().replace(/\/$/, "");

const nowIso = () => new Date().toISOString();

/**
 * Trigger a menu import and expose idle / importing / success / failed / unknown.
 *
 * @param {string} source
 */
export const useMenuImport = (source = "doordash") => {
  const { t } = useTranslation("menuImporters");
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [startedAt, setStartedAt] = useState(null);

  const mutation = useMutation({
    mutationFn: async (params) => {
      const baseUrl = menuImportApiUrl();
      if (!baseUrl) {
        throw new MenuImportFailure(t("status.apiNotConfigured"), {
          retryable: false,
          outcome: "failed",
        });
      }

      const businessIds = Array.isArray(params.businessIds)
        ? params.businessIds
        : [];
      const runRef = params.project
        ? { project: params.project, source }
        : null;
      const singleBusinessId = businessIds.length === 1 ? businessIds[0] : null;
      const runStartedAt = params.startedAt || nowIso();
      let run = null;
      const persist = (patch) => {
        if (!runRef) return;
        run = { ...run, ...patch };
        saveRun(runRef, run);
      };

      persist({
        status: "importing",
        startedAt: runStartedAt,
        finishedAt: null,
        sourceUrl: resolveSourceUrl(source, params),
        businessIds,
        businessNames: Array.isArray(params.businessNames)
          ? params.businessNames
          : [],
        appliedCount: null,
        errorMessage: null,
        menuBefore: singleBusinessId
          ? await fetchBusinessMenuSnapshot(singleBusinessId)
          : null,
        menuAfter: null,
      });

      const url = new URL(`${baseUrl}/api/import/${source}`);

      if (params.useFixture) {
        url.searchParams.set("useFixture", "true");
      }

      const headers = { "Content-Type": "application/json" };
      const token = apiClient.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const body = { ...params, source };
      delete body.useFixture;
      delete body.startedAt;
      delete body.businessNames;

      let response;
      try {
        response = await fetch(url.toString(), {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });
      } catch {
        persist({ status: "unknown" });
        throw new MenuImportFailure(t("errors.connectionLost"), {
          retryable: true,
          outcome: "unknown",
        });
      }

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      const detail =
        data && typeof data === "object"
          ? data.detail || data.message || data.error
          : null;

      if (!response.ok) {
        if ((response.status === 502 || response.status === 504) && !detail) {
          persist({ status: "unknown" });
          throw new MenuImportFailure(t("errors.connectionLost"), {
            retryable: true,
            outcome: "unknown",
          });
        }
        const verdict = classifyMenuImportError(
          source,
          detail,
          response.status,
          t,
        );
        persist({
          status: "failed",
          finishedAt: nowIso(),
          errorMessage: verdict.message,
        });
        throw new MenuImportFailure(verdict.message, {
          retryable: verdict.retryable,
          outcome: "failed",
        });
      }

      if (data?.success === false) {
        const verdict = classifyMenuImportError(
          source,
          data.error || data.message,
          response.status,
          t,
        );
        persist({
          status: "failed",
          finishedAt: nowIso(),
          errorMessage: verdict.message,
        });
        throw new MenuImportFailure(verdict.message, {
          retryable: verdict.retryable,
          outcome: "failed",
        });
      }

      persist({
        status: "success",
        finishedAt: nowIso(),
        appliedCount: data?.appliedCount ?? null,
        menuAfter: singleBusinessId
          ? await fetchBusinessMenuSnapshot(singleBusinessId)
          : null,
      });
      return data;
    },
    onSuccess: (data, variables) => {
      const count = data.appliedCount ?? variables.businessIds?.length ?? 0;
      const message =
        count <= 1
          ? t("status.success")
          : t("status.successToStores", { count });
      showToast(message, "success");
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
      queryClient.invalidateQueries({ queryKey: ["brand-businesses"] });
      queryClient.invalidateQueries({ queryKey: ["businesses-products"] });
      queryClient.removeQueries({ queryKey: ["business-by-slug"] });
      queryClient.removeQueries({ queryKey: ["category-products"] });
      queryClient.removeQueries({ queryKey: ["business-products-v2"] });
      queryClient.removeQueries({ queryKey: ["business-extras"] });
    },
  });

  const start = useCallback((params) => {
    const startedAt = nowIso();
    setStartedAt(startedAt);
    return { ...params, startedAt };
  }, []);

  const triggerImport = useCallback(
    (params, options) => mutation.mutate(start(params), options),
    [mutation, start],
  );

  const triggerImportAsync = useCallback(
    (params, options) => mutation.mutateAsync(start(params), options),
    [mutation, start],
  );

  const reset = useCallback(() => {
    setStartedAt(null);
    mutation.reset();
  }, [mutation]);

  const status = mutation.isPending
    ? "importing"
    : mutation.isSuccess
      ? "success"
      : mutation.isError
        ? mutation.error?.outcome === "unknown"
          ? "unknown"
          : "failed"
        : "idle";

  return {
    status,
    result: mutation.isSuccess
      ? {
          appliedCount: mutation.data?.appliedCount ?? null,
          jobId: mutation.data?.jobId ?? null,
        }
      : null,
    error: mutation.isError
      ? {
          message: mutation.error?.message,
          retryable: Boolean(mutation.error?.retryable),
        }
      : null,
    startedAt,
    triggerImport,
    triggerImportAsync,
    reset,
    isImporting: status === "importing",
  };
};

export default useMenuImport;
